import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Languages } from 'lucide-react';
import { HTML_LANG, LANGS, readStoredLang, setActiveLang, storeLang, t, type Lang } from './core';

interface I18nValue { lang: Lang; setLang: (lang: Lang) => void }

const I18nContext = createContext<I18nValue>({ lang: 'sr', setLang: () => undefined });

/**
 * Holds the language. Serbian unless the viewer chose English earlier (kept in localStorage, which may be unavailable).
 * `initial` overrides the stored choice (tests). Components that show text call `useI18n()` so they re-render on a switch.
 */
export function I18nProvider({ children, initial }: { children: ReactNode; initial?: Lang }) {
  const [lang, setLangState] = useState<Lang>(() => initial ?? readStoredLang());
  // Set during render so every child of this render already formats in the new language.
  setActiveLang(lang);
  useEffect(() => { document.documentElement.lang = HTML_LANG[lang]; }, [lang]);
  const setLang = useCallback((next: Lang) => { storeLang(next); setLangState(next); }, []);
  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

/** The header switch between Serbian and English. */
export function LanguageSwitcher() {
  const { lang, setLang } = useI18n();
  return <div className="language-switcher" role="group" aria-label={t('common.language')}>
    <Languages size={15} aria-hidden="true" />
    {LANGS.map((item) => <button key={item} type="button" lang={HTML_LANG[item]} aria-pressed={lang === item} className={lang === item ? 'active' : ''} onClick={() => setLang(item)}>
      {item.toUpperCase()}<span className="sr-only"> {t(`common.language.${item}`)}</span>
    </button>)}
  </div>;
}
